import React from 'react';
import PremiumLayout from './PremiumLayout';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { getArticles, Article, Paginated, getFeaturedArticles } from '../../services/articles';
import { assetUrl } from '../../utils/url';
import { Box, Heading, Text, Grid, GridItem, Skeleton, LinkBox, Image } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';

const BlogCard: React.FC<{ article: Article; variant?: 'large' | 'small' }> = ({ article, variant }) => {
  const link = article.slug ? `/news/${article.slug}` : `/articles/${article.id}`;
  const imageH =
    variant === 'large'
      ? ({ base: '260px', md: '360px' } as const)
      : variant === 'small'
      ? ({ base: '160px', md: '200px' } as const)
      : ({ base: '220px', md: '240px' } as const);

  return (
    <LinkBox
      as={RouterLink}
      to={link}
      borderRadius="lg"
      overflow="hidden"
      bg="rgba(0,0,0,0.4)"
      _hover={{ transform: 'translateY(-4px)', boxShadow: 'xl', bg: 'rgba(0,0,0,0.6)' }}
      transition="all 0.25s ease"
    >
      <Box position="relative">
        <Image
          src={assetUrl(article.image_url) || '/stadium-placeholder.jpg'}
          alt={article.title}
          w="100%"
          h={imageH}
          objectFit="cover"
        />
        <Box position="absolute" inset={0} bgGradient="linear(to-t, rgba(0,0,0,0.7), rgba(0,0,0,0.15))" />
        <Heading
          as="h3"
          fontSize={variant === 'large' ? { base: 'xl', md: '2xl' } : { base: 'lg', md: 'xl' }}
          fontWeight="800"
          letterSpacing="0.3px"
          textTransform="uppercase"
          position="absolute"
          bottom={3}
          left={4}
          right={4}
          color="white"
          noOfLines={2}
        >
          {article.title}
        </Heading>
      </Box>
    </LinkBox>
  );
};

const PremiumBlogPage: React.FC = () => {
  const pageSize = 18;
  const featuredQ = useQuery<Paginated<Article>>(
    ['articles-featured', { page_size: 3 }],
    () => getFeaturedArticles({ page_size: 3 }),
    { staleTime: 5 * 60 * 1000 }
  );
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery<Paginated<Article>>(
    ['articles-public', { page_size: pageSize, published: true }],
    ({ pageParam = 1 }) => getArticles({ page: pageParam, page_size: pageSize, published: true }),
    {
      getNextPageParam: (lastPage, allPages) => {
        const loaded = allPages.reduce((sum, p) => sum + (p?.data?.length || 0), 0);
        if (!lastPage) return undefined;
        if (loaded < (lastPage.total || 0)) return allPages.length + 1;
        return undefined;
      },
    }
  );
  const articles = data?.pages?.flatMap((p) => p?.data || []) || [];
  const featured = featuredQ.data?.data || [];
  const rest = articles.filter((a) => !featured.some((f) => f.id === a.id));

  const sentinelRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (!hasNextPage || !sentinelRef.current) return;
    const el = sentinelRef.current;
    const io = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: '400px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const primaryFeatured = featured[0];
  const secondaryFeatured = featured.slice(1, 3);

  return (
    <PremiumLayout>
      <Box className="sparta-container sparta-section" mb={4}>
        <Heading as="h1" size="2xl" textTransform="uppercase">
          Blog
        </Heading>
        <Text mt={2} opacity={0.8}>
          Nejnovější články, rozhovory a zprávy z klubu.
        </Text>
      </Box>

      {primaryFeatured && (
        <Box className="sparta-container sparta-section" mb={6}>
          <Grid templateColumns={{ base: '1fr', md: '2fr 1fr' }} gap={6}>
            <GridItem>
              <BlogCard article={primaryFeatured} variant="large" />
            </GridItem>
            {secondaryFeatured.length > 0 && (
              <GridItem>
                <Grid templateRows="repeat(2, minmax(0, 1fr))" gap={4}>
                  {secondaryFeatured.map((a) => (
                    <BlogCard key={a.id} article={a} variant="small" />
                  ))}
                </Grid>
              </GridItem>
            )}
          </Grid>
        </Box>
      )}

      <Box className="sparta-container sparta-section">
        <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={6}>
          {isLoading &&
            Array.from({ length: 9 }).map((_, i) => (
              <Skeleton key={i} h={{ base: '260px', md: '280px' }} borderRadius="lg" />
            ))}
          {!isLoading &&
            rest.map((a) => (
              <GridItem key={a.id}>
                <BlogCard article={a} />
              </GridItem>
            ))}
        </Grid>
        {isFetchingNextPage && (
          <Box mt={4} textAlign="center">
            <Text opacity={0.8}>Načítání…</Text>
          </Box>
        )}
        <Box ref={sentinelRef} h="1px" />
      </Box>
    </PremiumLayout>
  );
};

export default PremiumBlogPage;
